import { useEffect, useRef, useState } from 'react';
import { useEffectOnce, useSessionStorage, useWindowScroll } from 'react-use';

export type ExitIntentTrigger =
  | 'back-button'
  | 'mouse-out-top'
  | 'touch-scroll-up';

export const useExitIntent = (
  triggers: ExitIntentTrigger[],
  onExitIntent: () => void,
  args: unknown[] = []
): void => {
  const dontTrigger = (trigger: ExitIntentTrigger) =>
    !triggers.includes(trigger);

  // Scroll up to reveal mobile browser controls--------------------------------
  const { y } = useWindowScroll();
  const nowRef = useRef<number>(Date.now());
  const yRef = useRef<number>(0);

  useEffect(() => {
    const hasTouchScreen = 'ontouchstart' in window;
    if (dontTrigger('touch-scroll-up') || !hasTouchScreen || y === yRef.current)
      return;

    const now = Date.now();
    const ms = now - nowRef.current;
    const diffY = y - yRef.current;

    nowRef.current = now;
    yRef.current = y;

    if (diffY < -30 && ms <= 25) {
      onExitIntent();
    }
    // Allow args array to define dependencies onExitIntent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggers, y, ...args]);

  // Mouse over top of page-----------------------------------------------------
  useEffect(() => {
    if (dontTrigger('mouse-out-top')) return;

    const triggerIfMouseLeavesAbove = (event: MouseEvent): void => {
      if (event.clientY <= 0) onExitIntent();
    };

    document.addEventListener('mouseleave', triggerIfMouseLeavesAbove);

    return () => {
      document.removeEventListener('mouseleave', triggerIfMouseLeavesAbove);
    };
    // Allow args array to define dependencies inside onExitIntent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggers, ...args]);

  // Back button----------------------------------------------------------------
  const [isBackButtonPressed, setIsBackButtonPressed] = useState(false);
  const [isExitIntentHistoryModified, setIsExitIntentHistoryModified] =
    useSessionStorage<boolean>('isExitIntentHistoryModified', false);

  useEffectOnce(() => {
    if (dontTrigger('back-button') || isExitIntentHistoryModified) return;

    // Push a fake entry to the history so we can catch a back event
    history.pushState('ih-exit-intent', '');
    // Save in session that we did so we don't push fake entries on refresh
    setIsExitIntentHistoryModified(true);
  });

  useEffect(() => {
    if (dontTrigger('back-button') || isBackButtonPressed) return;

    const triggerIfFirstPress = (): void => {
      if (!isBackButtonPressed) {
        onExitIntent();
        setIsBackButtonPressed(true);
      }
    };

    window.addEventListener('popstate', triggerIfFirstPress);

    return () => {
      window.removeEventListener('popstate', triggerIfFirstPress);
    };
    // Allow args array to define dependencies inside onExitIntent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBackButtonPressed, setIsBackButtonPressed, triggers, ...args]);
};
